import { useMemo, useRef, useState } from 'react';
import { Avatar, Badge, Button, Menu, MenuItem, MenuDivider, Modal, ModalBody, ModalFooter, ModalHeader, Popover, useToast } from '@vd/ui';
import { MoreHorizontal, UserPlus, Mail, RotateCcw, Link2, Copy, Check, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { useAuth, useCurrentCompany } from '../../../features/auth/AuthProvider';
import { DestructiveConfirm } from '../../../features/modals/DestructiveConfirm';
import { ProjectAccessModal } from '../../../features/members/ProjectAccessModal';
import { ProjectAccessPicker } from '../../../features/members/ProjectAccessPicker';
import { minSeatsFor, useBillingSummary } from '../../../features/billing/useBillingSummary';
import { AddSeatsPanel } from './AddSeatsPanel';
import styles from '../Settings.module.css';

type DeliveryMode = 'email' | 'link' | 'both';

export function MembersTab() {
  const { currentCompanyId } = useAuth();
  const currentCompany = useCurrentCompany();
  const isOwner = currentCompany?.role === 'owner';
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: members = [] } = useQuery({
    queryKey: ['members', currentCompanyId],
    queryFn: () => currentCompanyId ? api.listMembers(currentCompanyId) : Promise.resolve([]),
    enabled: !!currentCompanyId,
  });
  // Project list for the per-member access modal. Owners-only fetch
  // (non-owners can't open the modal anyway and shouldn't trigger the
  // network call). Reuses the existing 'projects' query key so it
  // shares any cache the sidebar / picker already populated.
  const { data: companyProjects = [] } = useQuery({
    queryKey: ['projects', currentCompanyId],
    queryFn: () => currentCompanyId ? api.listProjects(currentCompanyId) : Promise.resolve([]),
    enabled: !!currentCompanyId && !!isOwner,
  });
  const { data: invites = [] } = useQuery({
    queryKey: ['invites', currentCompanyId],
    queryFn: () => currentCompanyId ? api.listInvites(currentCompanyId) : Promise.resolve([]),
    enabled: !!currentCompanyId,
  });
  // Seat math for the invite modal — owner-only endpoint, gated by isOwner
  // so non-owners don't get a 403 logged for nothing.
  const { data: seatSummary, refetch: refetchSeats } = useQuery({
    queryKey: ['seat-summary', currentCompanyId],
    queryFn: () => currentCompanyId ? api.getSeatSummary(currentCompanyId) : Promise.resolve(null),
    enabled: !!currentCompanyId && !!isOwner,
  });
  // No seat control offers fewer than the plan is sold with (Business: three; TOADO-662).
  const catalog = useBillingSummary(currentCompanyId ?? undefined)?.catalog;
  const seatFloor = seatSummary
    ? Math.max(minSeatsFor(seatSummary.plan, catalog), seatSummary.currentMembers + seatSummary.openInvites)
    : 1;

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteRole, setInviteRole] = useState<'owner' | 'member' | 'viewer'>('member');
  const [delivery, setDelivery] = useState<DeliveryMode>('email');
  const [submitting, setSubmitting] = useState(false);
  // Invite-time per-batch access. One policy applies to every email in
  // the batch (plan §2.4). Picking 'restricted' + projects here means
  // the invitee lands restricted from their first authenticated
  // request, not after the owner remembers to patch them.
  const [inviteAccessMode, setInviteAccessMode] = useState<'all_projects' | 'restricted'>('all_projects');
  const [inviteProjectIds, setInviteProjectIds] = useState<Set<string>>(new Set());
  const [inviteProjectSearch, setInviteProjectSearch] = useState('');
  // When the user is over-cap and clicks "Add seats", we open an inline
  // panel inside the modal instead of navigating away. Emails in the
  // textarea stay mounted so the user doesn't have to retype after
  // adjusting their seat count.
  const [addSeatsOpen, setAddSeatsOpen] = useState(false);
  // After a successful invite, show the magic-link URLs so the owner
  // can copy + paste them into Slack / a DM / etc. Cleared when the
  // modal closes.
  const [inviteResults, setInviteResults] = useState<Array<{ email: string; inviteUrl: string }> | null>(null);
  const [activityFor, setActivityFor] = useState<null | { name: string }>(null);
  // Per-project access modal target — null when closed. Stores the
  // member's current accessMode + grants so the modal opens pre-filled
  // without an extra fetch.
  const [accessFor, setAccessFor] = useState<null | {
    user: { id: string; name: string; email: string };
    accessMode: 'all_projects' | 'restricted';
    projectIds: string[];
  }>(null);

  // Parse the email textarea live so the seat math + over-cap warning
  // update as the user types. Same parsing as sendInvites uses on submit.
  const parsedEmails = useMemo(
    () => inviteEmails.split(/[,\s]+/).map((e) => e.trim()).filter(Boolean),
    [inviteEmails],
  );
  // Viewer invites are free (don't count against seatsPurchased), so the
  // over-cap warning only applies to owner / member invites.
  const isPaidRole = inviteRole !== 'viewer';
  const seatsNeeded = isPaidRole ? parsedEmails.length : 0;
  const seatsAvailable = seatSummary?.seatsAvailable ?? Infinity;
  const overCap = isPaidRole && seatsNeeded > seatsAvailable;
  const seatsShort = Math.max(0, seatsNeeded - seatsAvailable);

  const closeInviteModal = () => {
    setInviteOpen(false);
    setInviteEmails('');
    setInviteRole('member');
    setDelivery('email');
    setInviteResults(null);
    setAddSeatsOpen(false);
    setInviteAccessMode('all_projects');
    setInviteProjectIds(new Set());
    setInviteProjectSearch('');
  };

  const sendInvites = async () => {
    if (!currentCompanyId || submitting) return;
    if (parsedEmails.length === 0) return;
    setSubmitting(true);
    try {
      // Owner-role invites are always all-projects on the server
      // (owners can't be restricted). The picker is hidden for the
      // owner role, but the picker STATE persists across role changes
      // — without this coerce, a user who picked 'restricted' + some
      // projects then switched to owner would send a restricted body
      // and the server would reject the batch. Coerce here so the
      // user can flip role freely without losing the batch.
      const effectiveAccessMode: 'all_projects' | 'restricted' =
        inviteRole === 'owner' ? 'all_projects' : inviteAccessMode;
      const created = await api.inviteMembers(
        currentCompanyId,
        parsedEmails.map((email) => ({ email, role: inviteRole })),
        {
          delivery,
          accessMode: effectiveAccessMode,
          // Only send the project list when actually restricting —
          // for the all-projects case (including the owner coerce
          // above), undefined causes the server to clear any
          // junction rows that might have been written.
          projectIds: effectiveAccessMode === 'restricted' ? Array.from(inviteProjectIds) : undefined,
        },
      );
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['invites', currentCompanyId] }),
        refetchSeats(),
      ]);
      // For link / both delivery, swap the modal body for the
      // magic-link copy panel so the owner can grab the URLs. For
      // email-only delivery, just toast + close.
      if (delivery === 'email') {
        toast.push({
          variant: 'success',
          title: `Invited ${created.length} ${created.length === 1 ? 'person' : 'people'}`,
          description: created.map((i) => i.email).join(', '),
        });
        closeInviteModal();
      } else {
        setInviteResults(created.map((i) => ({ email: i.email, inviteUrl: i.inviteUrl })));
      }
    } catch (e) {
      const err = e as Error & { code?: string };
      if (err.code === 'SEAT_CAP_REACHED') {
        toast.push({
          variant: 'danger',
          title: 'Not enough seats',
          description: 'Add seats from the billing page, then try again.',
        });
      } else {
        toast.push({ variant: 'danger', title: 'Invite failed', description: err.message });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async (inv: { id: string; email: string }) => {
    try {
      const { inviteUrl } = await api.resendInvite(inv.id);
      toast.push({
        variant: 'success',
        title: 'Invite resent',
        description: inv.email,
        action: {
          label: 'Copy link',
          onClick: () => {
            void navigator.clipboard.writeText(inviteUrl);
          },
        },
      });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Resend failed', description: (e as Error).message });
    }
  };
  const revoke = async (inv: { id: string; email: string }) => {
    try {
      await api.revokeInvite(inv.id);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['invites', currentCompanyId] }),
        refetchSeats(),
      ]);
      toast.push({ variant: 'info', title: 'Invite revoked', description: inv.email });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Revoke failed', description: (e as Error).message });
    }
  };

  return (
    <>
      <div className={styles.rowSpread}>
        <div>
          <h1 className={styles.h1}>Members</h1>
          <p className={styles.sub}>Who has access to this company and what they can do.</p>
        </div>
        {isOwner && (
          <Button variant="primary" leftIcon={<UserPlus size={14} />} onClick={() => setInviteOpen(true)}>
            Invite members
          </Button>
        )}
      </div>

      {/* Decrease-seats panel: shows when the company is paying for
          more seats than are currently in use. One click to reduce
          billed seats to the floor (current members + open invites)
          or to a custom number. */}
      {isOwner && seatSummary && (seatSummary.plan === 'team' || seatSummary.plan === 'business') && currentCompanyId &&
        seatSummary.cap > seatFloor && (
          <SeatDecreasePanel
            companyId={currentCompanyId}
            seatSummary={seatSummary}
            floor={seatFloor}
            onApplied={() => { void refetchSeats(); }}
          />
        )}

      {/* Scrolls sideways in its own box when the window is narrow, rather
          than running its badges off the page (TOADO-793). */}
      <section className={`${styles.section} ${styles.tableScroll}`}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th><th>Email</th><th>Role</th><th>Access</th><th>Status</th><th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {members.map(({ user, role, accessMode, projectIds }) => (
              <MemberRow
                key={user.id}
                user={user}
                role={role}
                accessMode={accessMode}
                projectIds={projectIds}
                companyId={currentCompanyId ?? ''}
                canManage={!!isOwner}
                onViewActivity={() => setActivityFor({ name: user.name })}
                onManageAccess={() => setAccessFor({ user, accessMode, projectIds })}
              />
            ))}
            {invites.map((inv) => (
              <InviteRow
                key={inv.id}
                invite={inv}
                canManage={!!isOwner}
                onResend={() => resend(inv)}
                onRevoke={() => revoke(inv)}
              />
            ))}
          </tbody>
        </table>
      </section>

      <Modal open={inviteOpen} onClose={closeInviteModal} width={520}>
        <ModalHeader>{inviteResults ? 'Share these magic links' : 'Invite members'}</ModalHeader>
        <ModalBody>
          {inviteResults ? (
            <InviteLinkList results={inviteResults} delivery={delivery} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {seatSummary && (
                <SeatMeter
                  summary={seatSummary}
                  seatsNeeded={seatsNeeded}
                  overCap={overCap}
                  seatsShort={seatsShort}
                  onAddSeats={() => setAddSeatsOpen(true)}
                />
              )}
              {/* Inline seat-adjust panel. Replaces the older flow
                  that navigated away to the billing page (and lost
                  whatever the user had typed in the email textarea
                  below). The panel previews the prorated charge,
                  confirms, then refetches the seat summary so the
                  meter above flips to "you have enough seats." */}
              {addSeatsOpen && currentCompanyId && seatSummary && (seatSummary.plan === 'team' || seatSummary.plan === 'business') && (
                <AddSeatsPanel
                  companyId={currentCompanyId}
                  currentQuantity={seatSummary.cap}
                  minQuantity={seatFloor}
                  defaultQuantity={Math.max(seatSummary.cap, seatSummary.cap + seatsShort)}
                  intentLabel={
                    overCap
                      ? `You need ${seatsShort} more seat${seatsShort === 1 ? '' : 's'} to send these invites. Buy more if you'll need them soon; it saves a trip back here.`
                      : 'Adjust your paid seat count.'
                  }
                  onApplied={() => {
                    setAddSeatsOpen(false);
                    void refetchSeats();
                  }}
                  onCancel={() => setAddSeatsOpen(false)}
                />
              )}

              <label style={{ fontSize: 13, fontWeight: 500 }}>Email addresses</label>
              <textarea
                autoFocus
                value={inviteEmails}
                onChange={(e) => setInviteEmails(e.target.value)}
                placeholder="jane@acme.example, alex@acme.example"
                rows={3}
                style={{
                  width: '100%', padding: '8px 10px',
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 6, color: 'var(--text)',
                  fontSize: 14, fontFamily: 'inherit',
                  resize: 'vertical',
                }}
              />
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Separate multiple emails with commas or newlines.</span>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                <label style={{ fontSize: 13, fontWeight: 500 }}>Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'owner' | 'member' | 'viewer')}
                  style={{
                    padding: '6px 10px',
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    color: 'var(--text)',
                    fontSize: 13,
                  }}
                >
                  <option value="member">Member (paid seat)</option>
                  <option value="owner">Owner (paid seat)</option>
                  <option value="viewer">Viewer (free, read-only)</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                <label style={{ fontSize: 13, fontWeight: 500 }}>How should we send the invite?</label>
                <DeliveryToggle value={delivery} onChange={setDelivery} />
              </div>

              {/* Per-batch project access. Owner-role invites are
                  always all-projects regardless of the selection
                  (server-enforced); hide the picker rather than show
                  a disabled control so it's obvious from the UI side. */}
              {inviteRole !== 'owner' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                  <label style={{ fontSize: 13, fontWeight: 500 }}>Project access</label>
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                    Applies to every invite in this batch. You can change it later from each member's row.
                  </span>
                  <ProjectAccessPicker
                    accessMode={inviteAccessMode}
                    setAccessMode={setInviteAccessMode}
                    selected={inviteProjectIds}
                    setSelected={setInviteProjectIds}
                    search={inviteProjectSearch}
                    setSearch={setInviteProjectSearch}
                    projects={companyProjects}
                    subjectLabel={parsedEmails.length === 1 ? 'This member' : 'Each member'}
                  />
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {inviteResults ? (
            <Button variant="primary" onClick={closeInviteModal}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={closeInviteModal}>Cancel</Button>
              <Button
                variant="primary"
                leftIcon={delivery === 'link' ? <Link2 size={14} /> : <Mail size={14} />}
                disabled={
                  !inviteEmails.trim()
                  || submitting
                  || overCap
                  // Restricted-with-zero-projects mirrors the server
                  // validation. The server returns 400, but blocking
                  // it client-side saves the round-trip and shows the
                  // intent more clearly.
                  || (inviteRole !== 'owner' && inviteAccessMode === 'restricted' && inviteProjectIds.size === 0)
                }
                loading={submitting}
                onClick={sendInvites}
              >
                {delivery === 'link' ? 'Generate links' : delivery === 'both' ? 'Send & generate links' : 'Send invites'}
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>

      <Modal open={!!activityFor} onClose={() => setActivityFor(null)} width={560}>
        <ModalHeader>{activityFor?.name ?? 'Activity'}</ModalHeader>
        <ModalBody>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Per-member activity will pull from the audit log once mutation logging is wired.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setActivityFor(null)}>Close</Button>
        </ModalFooter>
      </Modal>

      {accessFor && currentCompanyId && (
        <ProjectAccessModal
          open={!!accessFor}
          onClose={() => setAccessFor(null)}
          companyId={currentCompanyId}
          user={accessFor.user}
          initialAccessMode={accessFor.accessMode}
          initialProjectIds={accessFor.projectIds}
          projects={companyProjects}
        />
      )}
    </>
  );
}

/**
 * Standalone "you're paying for unused seats" panel above the members
 * table. Renders only when seatsPurchased > seatsUsed (active members
 * + open invites). Click to expand the inline AddSeatsPanel pre-filled
 * with the floor; user can reduce to that or any custom number.
 */
function SeatDecreasePanel({
  companyId,
  seatSummary,
  floor,
  onApplied,
}: {
  companyId: string;
  seatSummary: { plan: string; cap: number; currentMembers: number; openInvites: number };
  /** The fewest seats it may go to: those in use, and never below the plan's minimum. */
  floor: number;
  onApplied: () => void;
}) {
  const [open, setOpen] = useState(false);
  const used = seatSummary.currentMembers + seatSummary.openInvites;
  const unused = Math.max(0, seatSummary.cap - floor);
  return (
    <section className={styles.section}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>
            {unused} unused paid seat{unused === 1 ? '' : 's'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            You're paying for {seatSummary.cap} seat{seatSummary.cap === 1 ? '' : 's'} but only {used} {used === 1 ? 'is' : 'are'} in use.
            Reducing your seat count credits the prorated unused portion to your next invoice.
          </div>
        </div>
        {!open && (
          <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
            Reduce seats
          </Button>
        )}
      </div>
      {open && (
        <div style={{ marginTop: 12 }}>
          <AddSeatsPanel
            companyId={companyId}
            currentQuantity={seatSummary.cap}
            minQuantity={floor}
            defaultQuantity={floor}
            intentLabel="Reduce your billed seats. Credit applies to your next invoice."
            onApplied={() => { setOpen(false); onApplied(); }}
            onCancel={() => setOpen(false)}
          />
        </div>
      )}
    </section>
  );
}

/**
 * Seat-math summary shown at the top of the invite modal. Switches to a
 * red-bordered over-cap warning + "Add seats" CTA when the requested
 * non-viewer invites would exceed `seatsAvailable`.
 */
function SeatMeter({
  summary,
  seatsNeeded,
  overCap,
  seatsShort,
  onAddSeats,
}: {
  summary: { plan: string; cap: number; currentMembers: number; openInvites: number; seatsAvailable: number };
  seatsNeeded: number;
  overCap: boolean;
  seatsShort: number;
  onAddSeats: () => void;
}) {
  const used = summary.currentMembers + summary.openInvites;
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 8,
        border: `1px solid ${overCap ? 'var(--danger)' : 'var(--border)'}`,
        background: overCap
          ? 'color-mix(in srgb, var(--danger) 8%, transparent)'
          : 'var(--bg-muted)',
        fontSize: 13,
        color: 'var(--text-secondary)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
        <span>
          <strong style={{ color: 'var(--text)' }}>
            {summary.plan.charAt(0).toUpperCase() + summary.plan.slice(1)} plan
          </strong>{' '}
          · {used} of {summary.cap} paid seat{summary.cap === 1 ? '' : 's'} used
          {seatsNeeded > 0 && (
            <span> · {seatsNeeded} more requested</span>
          )}
        </span>
      </div>
      {overCap && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--danger)' }}>
          <AlertTriangle size={14} />
          <span style={{ flex: 1 }}>
            Need {seatsShort} more seat{seatsShort === 1 ? '' : 's'}. Viewer invites are free if that fits your team.
          </span>
          <Button variant="primary" size="sm" onClick={onAddSeats}>
            Add seats
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Three-way segmented control for the delivery mode. Pure CSS, no
 * external dep — keeps the modal visually contained.
 */
function DeliveryToggle({
  value,
  onChange,
}: {
  value: DeliveryMode;
  onChange: (v: DeliveryMode) => void;
}) {
  const opts: Array<{ key: DeliveryMode; label: string; help: string }> = [
    { key: 'email', label: 'Email', help: 'Send a magic link to each address.' },
    { key: 'link',  label: 'Link only', help: 'Skip email. Show me the URL to share myself.' },
    { key: 'both',  label: 'Both', help: 'Email AND give me the URL.' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{
        display: 'flex',
        background: 'var(--bg-muted)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 3,
        gap: 3,
      }}>
        {opts.map((o) => (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.key)}
            style={{
              flex: 1,
              padding: '6px 10px',
              borderRadius: 5,
              fontSize: 13,
              fontWeight: value === o.key ? 600 : 500,
              background: value === o.key ? 'var(--surface)' : 'transparent',
              color: value === o.key ? 'var(--text)' : 'var(--text-secondary)',
              border: value === o.key ? '1px solid var(--border)' : '1px solid transparent',
              cursor: 'pointer',
              boxShadow: value === o.key ? 'var(--sh-sm)' : 'none',
            }}
          >
            {o.label}
          </button>
        ))}
      </div>
      <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
        {opts.find((o) => o.key === value)?.help}
      </span>
    </div>
  );
}

/** Post-submit list with a copy-to-clipboard button per invite URL. */
function InviteLinkList({
  results,
  delivery,
}: {
  results: Array<{ email: string; inviteUrl: string }>;
  delivery: DeliveryMode;
}) {
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const copy = async (idx: number, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedIdx(idx);
      window.setTimeout(() => setCopiedIdx((c) => (c === idx ? null : c)), 1500);
    } catch {
      /* clipboard may be denied; user can still select + copy manually */
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
        {delivery === 'both'
          ? 'Emails are on their way. You can also copy the links below to share directly.'
          : 'Send these links to your teammates however you like (Slack, DM, paper airplane).'}
      </p>
      {results.map((r, i) => (
        <div
          key={r.inviteUrl}
          style={{
            padding: 10,
            border: '1px solid var(--border)',
            borderRadius: 8,
            background: 'var(--bg-muted)',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 500 }}>{r.email}</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <code style={{
              flex: 1,
              fontSize: 12,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '6px 8px',
              color: 'var(--text)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              userSelect: 'all',
            }}>
              {r.inviteUrl}
            </code>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={copiedIdx === i ? <Check size={12} /> : <Copy size={12} />}
              onClick={() => copy(i, r.inviteUrl)}
            >
              {copiedIdx === i ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function MemberRow({
  user, role, accessMode, projectIds, companyId, canManage, onViewActivity, onManageAccess,
}: {
  user: { id: string; name: string; email: string };
  role: 'owner' | 'member' | 'viewer';
  accessMode: 'all_projects' | 'restricted';
  projectIds: string[];
  companyId: string;
  canManage: boolean;
  onViewActivity: () => void;
  onManageAccess: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [submenu, setSubmenu] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const qc = useQueryClient();

  const changeRole = async (r: 'owner' | 'member' | 'viewer') => {
    setOpen(false);
    setSubmenu(false);
    try {
      await api.updateMemberRole(companyId, user.id, r);
      await qc.invalidateQueries({ queryKey: ['members', companyId] });
      toast.push({
        variant: 'success',
        title: 'Role updated',
        description: `${user.name} is now ${r === 'owner' ? 'an Owner' : r === 'viewer' ? 'a Viewer' : 'a Member'}.`,
      });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Role change failed', description: (e as Error).message });
    }
  };

  const remove = async () => {
    try {
      await api.removeMember(companyId, user.id);
      // Invalidate both queries: 'members' so the row disappears, and
      // 'seat-summary' so the parent's SeatDecreasePanel re-evaluates
      // with the new lower seats-in-use count and auto-renders if the
      // company is now overpaying.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['members', companyId] }),
        qc.invalidateQueries({ queryKey: ['seat-summary', companyId] }),
      ]);
      toast.push({
        variant: 'info',
        title: 'Member removed',
        description: `${user.name} no longer has access. You can reduce your billed seats above the members list to credit the unused portion.`,
      });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Remove failed', description: (e as Error).message });
    }
  };

  return (
    <tr>
      <td>
        <div className={styles.row}>
          <Avatar name={user.name} />
          <span>{user.name}</span>
        </div>
      </td>
      <td style={{ color: 'var(--text-secondary)' }}>{user.email}</td>
      <td>
        {role === 'viewer' ? (
          <Badge variant="outline">Viewer · Free</Badge>
        ) : (
          <Badge variant={role === 'owner' ? 'solid' : 'outline'}>{role}</Badge>
        )}
      </td>
      <td>
        {role === 'owner' ? (
          <span title="Owners always see all projects" style={{ color: 'var(--text-tertiary)' }}>-</span>
        ) : accessMode === 'all_projects' ? (
          <Badge variant="outline">All projects</Badge>
        ) : canManage ? (
          <button
            type="button"
            onClick={onManageAccess}
            title="Manage project access"
            style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            <Badge variant="warning">
              {projectIds.length} project{projectIds.length === 1 ? '' : 's'}
            </Badge>
          </button>
        ) : (
          // Non-owners see the badge as a static indicator — only the
          // owner who set the restriction can click it to change.
          <Badge variant="warning">
            {projectIds.length} project{projectIds.length === 1 ? '' : 's'}
          </Badge>
        )}
      </td>
      <td><Badge variant="success" dot>Active</Badge></td>
      <td>
        <button
          ref={btn}
          onClick={() => setOpen((v) => !v)}
          aria-label="More"
          style={{ padding: 4, borderRadius: 4, color: 'var(--text-tertiary)' }}
        >
          <MoreHorizontal size={14} />
        </button>
        <Popover
          open={open}
          onClose={() => { setOpen(false); setSubmenu(false); }}
          anchor={btn.current?.getBoundingClientRect() ?? null}
          placement="bottom-end"
        >
          {submenu ? (
            <Menu>
              <div style={{ padding: '6px 8px', fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Change role
              </div>
              <MenuItem onClick={() => changeRole('owner')}>Owner {role === 'owner' && '●'}</MenuItem>
              <MenuItem onClick={() => changeRole('member')}>Member {role === 'member' && '●'}</MenuItem>
              <MenuItem onClick={() => changeRole('viewer')}>Viewer (read-only, free) {role === 'viewer' && '●'}</MenuItem>
              <MenuDivider />
              <MenuItem onClick={() => setSubmenu(false)}>← Back</MenuItem>
            </Menu>
          ) : (
            <Menu>
              {canManage && <MenuItem onClick={() => setSubmenu(true)}>Change role…</MenuItem>}
              {canManage && role !== 'owner' && (
                <MenuItem onClick={() => { setOpen(false); onManageAccess(); }}>
                  Manage project access…
                </MenuItem>
              )}
              <MenuItem onClick={() => { setOpen(false); onViewActivity(); }}>View activity</MenuItem>
              {canManage && (
                <>
                  <MenuDivider />
                  <MenuItem destructive onClick={() => { setOpen(false); setRemoveOpen(true); }}>
                    Remove from company…
                  </MenuItem>
                </>
              )}
            </Menu>
          )}
        </Popover>

        <DestructiveConfirm
          open={removeOpen}
          onClose={() => setRemoveOpen(false)}
          onConfirm={remove}
          title={`Remove ${user.name}?`}
          description="They'll lose access to all projects in this company immediately. Their comments and tickets remain."
          confirmLabel="Remove"
          holdToConfirm
        />
      </td>
    </tr>
  );
}

function InviteRow({
  invite, canManage, onResend, onRevoke,
}: {
  invite: {
    id: string; email: string; role: 'owner' | 'member' | 'viewer';
    accessMode: 'all_projects' | 'restricted';
    projectIds: string[];
    /** TOADO-243: null = staged by staff during provisioning — created
     *  but never emailed, and its link is inert until sent. Owners must
     *  be able to tell this from "invited and waiting". */
    sentAt?: string | null;
  };
  canManage: boolean;
  onResend: () => void;
  onRevoke: () => void;
}) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);

  return (
    <tr>
      <td>
        <div className={styles.row} style={{ color: 'var(--text-tertiary)' }}>
          <Avatar name={invite.email.split('@')[0] ?? '?'} />
          <span style={{ color: 'var(--text-secondary)' }}>(pending)</span>
        </div>
      </td>
      <td style={{ color: 'var(--text-secondary)' }}>{invite.email}</td>
      <td><Badge variant="outline">{invite.role}</Badge></td>
      {/* Show the invite's resolved access policy so the owner can
          confirm at a glance that a restricted invite was set up
          right BEFORE the invitee accepts — that's the whole point of
          moving access to invite time. */}
      <td>
        {invite.role === 'owner' ? (
          <span title="Owners always see all projects" style={{ color: 'var(--text-tertiary)' }}>All projects</span>
        ) : invite.accessMode === 'all_projects' ? (
          <Badge variant="outline">All projects</Badge>
        ) : (
          <Badge variant="warning">
            {invite.projectIds.length} project{invite.projectIds.length === 1 ? '' : 's'}
          </Badge>
        )}
      </td>
      <td>
        {invite.sentAt === null
          ? <Badge variant="outline" dot>Staged, not sent</Badge>
          : <Badge variant="warning" dot>Invited</Badge>}
      </td>
      <td>
        {canManage && (
          <>
            <button
              ref={btn}
              onClick={() => setOpen((v) => !v)}
              aria-label="Invite actions"
              style={{ padding: 4, borderRadius: 4, color: 'var(--text-tertiary)' }}
            >
              <MoreHorizontal size={14} />
            </button>
            <Popover open={open} onClose={() => setOpen(false)} anchor={btn.current?.getBoundingClientRect() ?? null} placement="bottom-end">
              <Menu>
                <MenuItem leftIcon={<RotateCcw size={14} />} onClick={() => { setOpen(false); onResend(); }}>
                  {invite.sentAt === null ? 'Send & copy link' : 'Resend & copy link'}
                </MenuItem>
                <MenuDivider />
                <MenuItem destructive onClick={() => { setOpen(false); onRevoke(); }}>
                  Revoke invite
                </MenuItem>
              </Menu>
            </Popover>
          </>
        )}
      </td>
    </tr>
  );
}
